// Shared window chrome theme, extracted from SiteSetupWindow so every main-menu window
// paints its header row, buttons, tables and inputs against the same palette. All colors
// derive from `global.gw.theme`, so a `themeChanged` event on any window re-renders the rest.

import * as global from "../../data/global";
import { MuiMaterials } from "../../@index.js";
import {
    BRAND_GOLD,
    BRAND_GOLD_BORDER,
    BRAND_GOLD_MID,
} from "./brandGold.js";
import { withMenuScrollHintTheme } from "./ScrollHintPaper.js";

/** Read a light-or-dark color pair against the current global theme. */
export function getWindowChromeThemeColor(lightColor, darkColor) {
    return global.gw.theme === "light" ? lightColor : darkColor;
}

/**
 * Build the palette tokens used by WindowChrome and the shared SiteSelectPhase, along with the
 * MUI theme those components inject via ThemeProvider. Kept intentionally minimal: only the
 * overrides Site Setup relies on, so custom per-window MUI theming still composes on top.
 */
export function createWindowChromeTheme() {
    const isLight = global.gw.theme === "light";
    const textPrimary = isLight ? "#333333" : BRAND_GOLD;
    const textSecondary = isLight ? "#666666" : BRAND_GOLD_MID;
    const paper = isLight ? "#ffffff" : "#2d2d2d";
    const border = isLight ? "#cccccc" : BRAND_GOLD_BORDER;
    const pageBg = isLight ? "#e8e8e8" : "#1a1a1a";

    return {
        isLight,
        textPrimary,
        textSecondary,
        paper,
        border,
        pageBg,
        theme: MuiMaterials.createTheme({
            palette: {
                mode: isLight ? "light" : "dark",
                text: { primary: textPrimary, secondary: textSecondary },
                background: { default: isLight ? "#f1f1f1" : "#121212", paper },
                primary: { main: BRAND_GOLD },
            },
            components: withMenuScrollHintTheme({
                MuiTypography: {
                    styleOverrides: { root: { color: textPrimary } },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: isLight ? "#000000" : BRAND_GOLD,
                            "&.Mui-focused": { color: isLight ? "#000000" : BRAND_GOLD },
                        },
                        // Custom OutlinedInput padding (6px 8px) does not match MUI's default
                        // label translate, so empty outlined labels sit too high. Center them
                        // in the field until they shrink to the notch.
                        outlined: {
                            "&:not(.MuiInputLabel-shrink)": {
                                top: "50%",
                                transform: "translate(14px, -50%) scale(1)",
                            },
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: textPrimary,
                            fontSize: 13,
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: isLight ? "#666666" : BRAND_GOLD,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: isLight ? "#666666" : BRAND_GOLD,
                            },
                        },
                        input: { padding: "6px 8px" },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            textTransform: "none",
                            color: textPrimary,
                            backgroundColor: "transparent",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.875rem",
                            lineHeight: 1.25,
                            paddingTop: 6,
                            paddingBottom: 6,
                            minHeight: 36,
                            boxShadow: "none",
                            "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                                display: "inline-flex",
                                alignItems: "center",
                                marginTop: 0,
                                marginBottom: 0,
                            },
                        },
                        outlined: {
                            borderColor: isLight ? "#999999" : BRAND_GOLD,
                            backgroundColor: "transparent",
                            color: textPrimary,
                            "&:hover": {
                                borderColor: isLight ? "#666666" : BRAND_GOLD,
                                backgroundColor: isLight
                                    ? "rgba(0,0,0,0.04)"
                                    : "rgba(244,204,142,0.08)",
                                boxShadow: "none",
                            },
                        },
                        contained: {
                            color: isLight ? "#ffffff" : BRAND_GOLD,
                            backgroundColor: isLight ? "#424242" : "#000000",
                            boxShadow: "none",
                            "&:hover": {
                                backgroundColor: isLight ? "#616161" : "#1a1a1a",
                                boxShadow: "none",
                            },
                        },
                        text: {
                            backgroundColor: "transparent",
                            color: textPrimary,
                            "&:hover": {
                                backgroundColor: isLight
                                    ? "rgba(0,0,0,0.04)"
                                    : "rgba(244,204,142,0.08)",
                            },
                        },
                        sizeSmall: {
                            fontSize: "0.8125rem",
                            lineHeight: 1,
                            padding: "6px 12px",
                            minHeight: 32,
                        },
                    },
                },
                MuiLoadingButton: {
                    styleOverrides: {
                        root: {
                            textTransform: "none",
                        },
                    },
                },
                MuiIconButton: {
                    styleOverrides: { root: { color: textPrimary } },
                },
                MuiCheckbox: {
                    styleOverrides: { root: { color: textPrimary } },
                },
                MuiMenuItem: {
                    styleOverrides: { root: { color: textPrimary } },
                },
                MuiSelect: {
                    styleOverrides: {
                        icon: { color: textPrimary },
                    },
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: { color: textPrimary, borderColor: border, fontSize: 13 },
                        head: {
                            fontWeight: 600,
                            backgroundColor: isLight ? "#f5f5f5" : "#252525",
                            color: textPrimary,
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: "none",
                            backgroundColor: paper,
                            color: textPrimary,
                        },
                    },
                },
                MuiSvgIcon: {
                    styleOverrides: { root: { color: textPrimary } },
                },
            }),
        }),
    };
}
